// 🚀 WJPNCMA - 우주PNC CI/CD 플랫폼 설정 파일
export const AppConfig = {
  // 📱 앱 기본 정보
  app: {
    name: "WJPNCMA",
    shortName: "WJPNC",
    description: "우주PNC CI/CD 플랫폼 - ODT 원격 관리 시스템",
    version: "0.1.6",
    author: "우주PNC",
    url: "https://wjpncma.com",
    email: "support@wjpnc.com",
  },

  // 🎨 브랜딩 & 테마
  branding: {
    logo: {
      text: "WJPNCMA",
      icon: "⚙️", // ODT 관리 시스템을 나타내는 아이콘
      url: "/", // 로고 클릭 시 이동할 URL
    },
    colors: {
      primary: "blue",
      secondary: "slate",
      accent: "green", // 기술/엔지니어링 느낌의 초록색
    },
    // 📱 다양한 브랜딩 템플릿
    theme: "modern", // modern | minimal | corporate | creative
  },

  // 🔧 기능 토글 (On/Off)
  features: {
    // 🔐 인증 관련
    auth: {
      enabled: true,
      providers: ["email"], // 기업용이므로 이메일 인증만
      registration: false, // 관리자가 직접 계정 생성
      emailVerification: true,
      passwordReset: true,
    },
    
    // 🎨 UI/UX 기능
    ui: {
      darkMode: true,
      animations: true,
      sound: false,
      breadcrumbs: true,
      search: true, // ODT Config 검색 기능 중요
      notifications: true, // 실시간 알림 중요
      help: true,
    },
    
    // 🌐 다국어
    i18n: {
      enabled: false, // 일단 한국어만
      languages: ["ko"],
      defaultLanguage: "ko",
    },
    
    // 📊 분석 & 모니터링
    analytics: {
      enabled: true,
      google: false,
      hotjar: false,
      customEvents: true, // ODT 사용 통계 수집
    },
    
    // 💳 결제 시스템
    payment: {
      enabled: false, // 내부 시스템이므로 불필요
      providers: [],
      currency: "KRW",
    },
    
    // 📧 이메일 시스템
    email: {
      enabled: true, // 알림 기능을 위해 필요
      provider: "nodemailer",
      templates: true,
    },
    
    // 📱 API 기능
    api: {
      rest: true,
      graphql: false,
      rateLimit: true,
      cors: true,
      swagger: true, // API 문서화 중요
      phpCompatibility: false,
    },

    // 🔌 WebSocket 기능 (ODT 실시간 연동)
    realtime: {
      enabled: true,
      websocket: true,
      odtAgent: true, // ODT Agent 연동
      remoteControl: true, // 원격 제어 기능
      monitoring: true, // 실시간 모니터링
    },
  },

  // 🗃️ 데이터베이스 설정
  database: {
    provider: "postgresql", // 엔터프라이즈용
    orm: "prisma",
    enableMigrations: true,
    enableSeeding: true,
    enableBackup: true,
  },

  // 📁 파일 & 미디어
  media: {
    upload: {
      enabled: true,
      maxSize: "50MB", // ODT Config 파일 업로드
      allowedTypes: ["document", "config"], // XML, JSON 등
      storage: "local",
    },
    cdn: {
      enabled: false,
      provider: "cloudflare",
    },
  },

  // 🚀 배포 & 환경
  deployment: {
    environment: (process.env.NODE_ENV || 'development') as EnvironmentType,
    provider: "onpremise" as const, // 온프레미스 배포
    domain: "localhost:3000",
    ssl: false,
    cdn: false,
  },

  // 🏗️ 메뉴 템플릿 설정 - CRM 템플릿 사용
  menu: {
    template: "crm" as MenuTemplate, // CRM 템플릿이 주유소 관리에 적합
    customization: {
      allowCollapse: true,
      showIcons: true,
      showBadges: true, // 알림 배지 표시
      grouping: true,
    },
  },

  // 🔒 보안 설정
  security: {
    csrf: true,
    rateLimit: {
      enabled: true,
      requests: 100,
      window: "15m",
    },
    encryption: true,
    headers: {
      hsts: true,
      xframe: "DENY",
      contentType: true,
    },
  },
} as const;

// 🏗️ 타입 정의
export type AppConfigType = typeof AppConfig;
export type MenuTemplate = 'crm' | 'ecommerce' | 'blog' | 'admin' | 'saas' | 'custom';
export type ThemeType = 'modern' | 'minimal' | 'corporate' | 'creative';
export type EnvironmentType = 'development' | 'staging' | 'production';

// 🔧 환경별 설정 오버라이드
export const getEnvironmentConfig = (env: EnvironmentType) => {
  switch (env) {
    case 'production':
      return {
        deployment: {
          environment: 'production' as const,
          provider: 'vercel' as const,
          domain: "your-domain.com",
          ssl: true,
          cdn: true,
        },
        security: {
          csrf: true,
          rateLimit: {
            enabled: true,
            requests: 60,
            window: "15m",
          },
          encryption: true,
          headers: {
            hsts: true,
            xframe: "DENY" as const,
            contentType: true,
          },
        },
      };
    case 'staging':
      return {
        deployment: {
          environment: 'staging' as const,
          provider: 'vercel' as const,
          domain: "staging.your-domain.com",
          ssl: true,
          cdn: false,
        },
      };
    default:
      return {};
  }
};

// 🎯 설정 유틸리티 함수들
export const isFeatureEnabled = (feature: string): boolean => {
  const keys = feature.split('.');
  let current: any = AppConfig.features;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return false;
    }
  }
  
  return Boolean(current);
};

// 예시 사용법:
// isFeatureEnabled('auth.enabled') // true
// isFeatureEnabled('payment.enabled') // false 